/**
 * Reels en mobile — avance automático y navegación explícita.
 *
 * En pantallas chicas la grilla de la sección "Por qué" se convierte en una
 * fila que se desliza (ver layout/responsive.css). Deslizar es cómodo, pero
 * nada indica cuántos reels hay ni en cuál estás: este módulo agrega los
 * puntos de posición y hace que la fila avance sola cada N segundos.
 *
 * El avance se frena apenas el reel importa más que el recorrido:
 *
 *   - mientras el usuario desliza, y unos segundos después;
 *   - cuando la sección no está en pantalla o la pestaña está en segundo plano;
 *   - de forma definitiva, cuando alguien toca un reel para reproducirlo.
 *
 * Ese último caso no se puede consultar: el embed de Instagram es de otro
 * origen y no cuenta qué está haciendo su reproductor. Se deduce del foco —
 * al tocar dentro de un iframe cross-origin la ventana pierde el foco y el
 * `activeElement` pasa a ser ese iframe. Es el único indicio confiable, y
 * ante la duda conviene equivocarse para el lado de no mover nada.
 *
 * Solo actúa en el layout mobile; en desktop la grilla es una grilla y no
 * hay nada que desplazar.
 */

/** Espejo del breakpoint de layout/responsive.css. */
const MOBILE_QUERY = '(max-width: 900px)';
/** Cada cuánto avanza un reel, en ms. */
const STEP_DELAY = 6000;
/** Pausa después de que el usuario desliza con el dedo. */
const IDLE_DELAY = 9000;
/** Pausa extra al volver del último reel al primero. */
const LOOP_PAUSE = 1.7;

function initOne(grid) {
  const cards = Array.from(grid.querySelectorAll('.video-card'));
  if (cards.length < 2) return;

  const mobile = window.matchMedia(MOBILE_QUERY);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---- Puntos de posición ------------------------------------------------
     Se generan acá y no en el HTML porque son puramente una ayuda de
     navegación: sin JS no habría nada que navegar con ellos.
     ---------------------------------------------------------------------- */

  const nav = document.createElement('div');
  nav.className = 'reels-nav';

  const dots = document.createElement('div');
  dots.className = 'reels-nav__dots';
  dots.setAttribute('role', 'group');
  dots.setAttribute('aria-label', 'Reels de Serendipia');

  const buttons = cards.map((card, i) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'reels-nav__dot';
    dot.setAttribute('aria-label', `Ver el reel ${i + 1} de ${cards.length}`);
    dot.addEventListener('click', () => {
      pauseForUser();
      goTo(i);
    });
    dots.append(dot);
    return dot;
  });

  const hint = document.createElement('p');
  hint.className = 'reels-nav__hint';
  hint.textContent = 'Deslizá para ver más reels · tocá para reproducir';

  nav.append(dots, hint);
  grid.after(nav);

  /* ---- Posición ----------------------------------------------------------- */

  const stepSize = () => {
    const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
    return cards[0].getBoundingClientRect().width + gap;
  };

  let current = 0;

  const syncDots = () => {
    const index = Math.min(cards.length - 1, Math.round(grid.scrollLeft / stepSize()));
    if (index === current) return;
    current = index;
    buttons.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  };

  const goTo = (index) => {
    grid.scrollTo({
      left: index * stepSize(),
      behavior: reduced.matches ? 'auto' : 'smooth',
    });
  };

  buttons[0].classList.add('is-active');
  buttons[0].setAttribute('aria-current', 'true');

  grid.addEventListener('scroll', syncDots, { passive: true });

  /* ---- Avance automático -------------------------------------------------- */

  let timer = null;
  let idleTimer = null;
  const blockers = new Set();

  const stop = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = (wait = STEP_DELAY) => {
    stop();
    if (blockers.size || reduced.matches || !mobile.matches) return;
    timer = setTimeout(advance, wait);
  };

  /** ¿Hay un reel con el foco puesto? Es el indicio de que se está mirando. */
  const reelHasFocus = () => {
    const el = document.activeElement;
    return el?.tagName === 'IFRAME' && grid.contains(el);
  };

  const advance = () => {
    timer = null;
    const last = cards.length - 1;

    // Doble control además del evento `blur` de más abajo: si el navegador no
    // lo dispara, acá igual nos enteramos antes de mover nada.
    if (reelHasFocus()) return block('playing');

    if (current >= last) {
      goTo(0);
      // Volver al principio es un salto de varias pantallas: se le da más
      // aire que a un avance normal.
      schedule(STEP_DELAY * LOOP_PAUSE);
      return;
    }
    goTo(current + 1);
    schedule();
  };

  const block = (reason) => {
    blockers.add(reason);
    stop();
  };

  const unblock = (reason, wait = STEP_DELAY) => {
    blockers.delete(reason);
    if (blockers.size) return;
    schedule(wait);
  };

  /** El usuario tomó el control del deslizamiento: se le deja el paso. */
  const pauseForUser = () => {
    nav.classList.add('has-interacted');
    block('user');
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => unblock('user'), IDLE_DELAY);
  };

  ['touchstart', 'pointerdown', 'wheel'].forEach((type) => {
    grid.addEventListener(type, pauseForUser, { passive: true });
  });

  /* Alguien puso a andar un reel: a partir de acá no se mueve más nada.
     No hay forma de saber cuándo termina o si lo pausó, y mover la fila
     debajo de un video que se está reproduciendo es lo peor que puede pasar. */
  window.addEventListener('blur', () => {
    if (!reelHasFocus()) return;
    block('playing');
    nav.classList.add('has-interacted');
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) block('hidden');
    else unblock('hidden');
  });

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? unblock('offscreen') : block('offscreen')),
      { threshold: 0.4 }
    );
    io.observe(grid);
    block('offscreen');
  }

  /* En desktop la grilla no se desplaza: el avance se apaga y los puntos los
     esconde el CSS. Al girar el teléfono vuelve solo. */
  const syncViewport = () => {
    if (mobile.matches) unblock('desktop');
    else block('desktop');
  };
  mobile.addEventListener('change', syncViewport);
  syncViewport();

  schedule();
}

export function init() {
  document.querySelectorAll('.video-grid').forEach(initOne);
}
